import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { evidenceAPI } from '../api';


const hasSelectedValue = (value) => (
    value !== '' && value !== '0' && value !== false && value !== null && value !== undefined
);

const formatEvidenceValue = (rawValue) => {
    if (rawValue === null || rawValue === undefined) return '';
    if (typeof rawValue === 'string') return rawValue;
    if (typeof rawValue === 'number' || typeof rawValue === 'boolean') return String(rawValue);

    if (Array.isArray(rawValue)) {
        return rawValue
            .map((item) => (item === null || item === undefined ? '' : String(item).trim()))
            .filter(Boolean)
            .join(', ');
    }

    if (typeof rawValue === 'object') {
        const selectedKeys = Object.keys(rawValue).filter((key) => hasSelectedValue(rawValue[key]));
        if (selectedKeys.length > 0) return selectedKeys.join(', ');

        try {
            return JSON.stringify(rawValue);
        } catch (error) {
            return '';
        }
    }

    return '';
};

const OriginalEvidence = ({ value, label = "Field Name", fnolId, fieldKey, fieldAliases = [], hasClaimId }) => {
    const [showModal, setShowModal] = useState(false);
    const [evidenceText, setEvidenceText] = useState('');
    const [initialText, setInitialText] = useState('');
    const [recordExists, setRecordExists] = useState(false);
    const [fullData, setFullData] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const textareaRef = useRef(null);
    const candidateFieldKeys = [fieldKey, ...fieldAliases].filter(Boolean);
    const originalEvidenceValue = formatEvidenceValue(value);

    const getFieldValue = (data) => {
        if (!data || typeof data !== 'object') return '';

        for (const key of candidateFieldKeys) {
            if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
            const fieldValue = data[key];
            if (fieldValue !== null && fieldValue !== undefined) {
                return fieldValue;
            }
        }

        return '';
    };

    const getWriteKey = (data) => {
        if (!data || typeof data !== 'object') return fieldKey;

        for (const key of candidateFieldKeys) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                return key;
            }
        }

        return fieldKey;
    };

    useEffect(() => {
        if (isEditing && textareaRef.current) {
            textareaRef.current.focus();
            const length = textareaRef.current.value.length;
            textareaRef.current.setSelectionRange(length, length);
        }
    }, [isEditing]);

    useEffect(() => {
        if (showModal && fnolId && fieldKey) {
            setIsEditing(false);
            setLoading(true);
            evidenceAPI.getAddedEvidence(fnolId)
                .then((res) => {
                    if (res.success) {
                        setRecordExists(true);
                        setFullData(res.data || {});
                        const val = formatEvidenceValue(getFieldValue(res.data));
                        setEvidenceText(val);
                        setInitialText(val);
                    } else if (res.notFound) {
                        setRecordExists(false);
                        setFullData({});
                        setEvidenceText('');
                        setInitialText('');
                    }
                })
                .catch(err => console.error(err))
                .finally(() => setLoading(false));
        }
    }, [showModal, fnolId, fieldKey]);

    const handleSave = async () => {
        if (!fnolId) return;

        setSaving(true);
        try {
            // Merge existing data with new field value to prevent overwriting other fields
            // if the backend expects a full object.
            const writeKey = getWriteKey(fullData);
            const payload = { ...fullData, [writeKey]: evidenceText };

            if (recordExists) {
                await evidenceAPI.updateAddedEvidence(fnolId, payload);
            } else {
                await evidenceAPI.createAddedEvidence(fnolId, payload);
                setRecordExists(true);
            }

            // Update initial text to new saved text so button disables
            setInitialText(evidenceText);

            // Update local fullData
            setFullData(payload);
            setIsEditing(false);

        } catch (error) {
            console.error("Failed to save evidence:", error);
            // Optionally show error toast
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = evidenceText !== initialText;

    return (
        <>
            <div onClick={() => setShowModal(true)} style={{ cursor: 'pointer', display: 'inline-block' }}>
                <img src="/OriginalEvidenceIcon.png" alt="Evidence Icon" style={{ width: '15px', height: '15px' }} />
                {/* <span className="ms-1">*</span> */}
            </div>

            {showModal && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 9999 }}>
                    <div className="bg-white rounded-4 shadow p-4" style={{ width: '600px', maxWidth: '95%' }}>
                        {/* Header */}
                        <div className="d-flex justify-content-center position-relative mb-4">
                            <h4 className="fw-bold mb-0">{label}</h4>
                            <button
                                onClick={() => setShowModal(false)}
                                className="btn-close position-absolute top-0 end-0"
                                aria-label="Close"
                                style={{ fontSize: '1.2rem' }}
                            ></button>
                        </div>

                        {/* Body */}
                        <div className="mb-3">
                            <label className="form-label fw-semibold">Evidence View</label>
                            <textarea
                                className="form-control py-2"
                                value={originalEvidenceValue}
                                readOnly
                                disabled
                                rows={3}
                                style={{ backgroundColor: '#f8f9fa', color: '#495057', resize: 'none' }}
                            />
                        </div>

                        <div className="mb-4">
                            {(initialText || isEditing) && (
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <label className="form-label fw-semibold mb-0">Adjusted Annotation</label>
                                </div>
                            )}

                            {!isEditing && initialText && (
                                <textarea
                                    className="form-control"
                                    rows="2"
                                    value={initialText}
                                    readOnly
                                    disabled
                                    style={{ backgroundColor: '#f8f9fa', color: '#495057', resize: 'none' }}
                                ></textarea>
                            )}

                            {isEditing && (
                                <textarea
                                    ref={textareaRef}
                                    className="form-control"
                                    rows="2"
                                    placeholder="Add Evidence Here"
                                    value={evidenceText}
                                    onChange={(e) => setEvidenceText(e.target.value)}
                                    disabled={loading || saving}
                                    style={{ color: '#495057', resize: 'none' }}
                                ></textarea>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="d-flex justify-content-end">
                            <button
                                className="btn text-white px-5 py-2 rounded-3"
                                onClick={() => {
                                    if (isEditing) {
                                        handleSave();
                                    } else {
                                        setEvidenceText(initialText);
                                        setIsEditing(true);
                                    }
                                }}
                                disabled={isEditing ? (!hasChanges || loading || saving) : (loading || hasClaimId)}
                                style={{
                                    backgroundColor: (isEditing ? (!hasChanges || loading || saving) : (loading || hasClaimId)) ? '#6c757d' : '#006BB3',
                                    fontWeight: '600',
                                    cursor: (isEditing ? (!hasChanges || loading || saving) : (loading || hasClaimId)) ? 'not-allowed' : 'pointer'
                                }}
                                title={hasClaimId ? "Claim ID already generated" : ""}
                            >
                                {isEditing ? (saving ? 'Saving...' : 'Save') : 'Edit'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default OriginalEvidence;
